import React, { Component } from "react";
import moment from "moment";
import { userService, tailgateService, noteService, selectService } from "services";
import {
  USER_TYPE_ADMIN,
  USER_TYPE_CHARGE,
  USER_TYPE_CONTREMAITRE,
  NOTE_TYPE_SSE,
} from "constants/dbValues";

const GRID_USER_TYPES = [USER_TYPE_CONTREMAITRE, USER_TYPE_CHARGE, USER_TYPE_ADMIN];

const TYPE_ORDER = GRID_USER_TYPES.reduce((order, type, i) => ({ ...order, [type]: i }), {});

const ROWS_PER_BATCH = 14;
const BATCH_MS = 10000;
const ROW_HEIGHT_FALLBACK = 38;
const ROLL_MS = 600; // keep in sync with the .tg-track transition

const DAY_LABELS = ["L", "M", "M", "J", "V"];
const WEEKEND_LABELS = ["S", "D"];

class TailgateGrid extends Component {
  constructor(props) {
    super(props);
    this.state = {
      foremen: [],
      tailgates: [],
      sseNotes: [],
      sseTotals: {},
      risks: {},
      loading: true,
      batch: 0,
      rowHeight: ROW_HEIGHT_FALLBACK,
      snapping: false,
    };
    this.trackRef = React.createRef();
    this.fetchData = this.fetchData.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.projectId !== this.props.projectId ||
      prevProps.clientId !== this.props.clientId
    ) {
      this.fetchData();
    }
    this.measureRowHeight();
  }

  componentWillUnmount() {
    this._mounted = false;
    clearInterval(this.rollTimer);
    clearTimeout(this.snapTimer);
  }

  measureRowHeight() {
    const row = this.trackRef.current && this.trackRef.current.children[0];
    if (!row) return;

    const height = row.getBoundingClientRect().height;
    if (height && Math.abs(height - this.state.rowHeight) > 0.5) {
      this.setState({ rowHeight: height });
    }
  }

  batchCount() {
    return Math.max(1, Math.ceil(this.state.foremen.length / ROWS_PER_BATCH));
  }

  batchOffset() {
    const { batch, rowHeight } = this.state;
    return batch * ROWS_PER_BATCH * rowHeight;
  }

  startRolling(total) {
    clearInterval(this.rollTimer);
    clearTimeout(this.snapTimer);
    this.setState({ batch: 0, snapping: false });
    if (total <= ROWS_PER_BATCH) return;

    this.rollTimer = setInterval(() => this.advance(), BATCH_MS);
  }

  // Rolls one position past the last batch onto a clone of the first, then jumps
  // home with the transition off so the loop never scrolls backwards.
  advance() {
    const clonePosition = this.batchCount();
    const next = this.state.batch + 1;

    this.setState({ batch: next, snapping: false });
    if (next < clonePosition) return;

    clearTimeout(this.snapTimer);
    this.snapTimer = setTimeout(() => {
      if (!this._mounted) return;
      this.setState({ batch: 0, snapping: true }, () => {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => this._mounted && this.setState({ snapping: false })),
        );
      });
    }, ROLL_MS + 200);
  }

  fetchData() {
    const { projectId, clientId } = this.props;

    const lastWeekStart = moment().subtract(1, "week").startOf("isoWeek");
    const thisWeekEnd = moment().endOf("isoWeek");
    const yearStart = moment().startOf("year");
    const throughToday = moment().add(1, "day").format("YYYY-MM-DD");

    const extraFilters = {
      ...(projectId ? { project_id: projectId } : {}),
      ...(clientId ? { client_id: clientId } : {}),
    };

    const periodDates = [lastWeekStart.format("YYYY-MM-DD"), thisWeekEnd.format("YYYY-MM-DD")];
    const yearDates = [yearStart.format("YYYY-MM-DD"), throughToday];

    this.setState({ loading: true });

    Promise.all([
      userService.list({
        type_id: GRID_USER_TYPES,
        disabled: 0,
        system_user: 0, // keep integration/service accounts off the board
        per_page: 1000,
        ...extraFilters,
      }),
      tailgateService.list({ date: periodDates, per_page: 1000, ...extraFilters }),
      noteService.list({
        type_id: NOTE_TYPE_SSE,
        date: periodDates,
        per_page: 1000,
        ...extraFilters,
      }),
      noteService.list({
        type_id: NOTE_TYPE_SSE,
        date: yearDates,
        per_page: 2000,
        ...extraFilters,
      }),
      selectService.listRisks(),
    ])
      .then(([foremenRes, tailgatesRes, sseRes, allYearRes, risksRes]) => {
        const foremen = (foremenRes.data || foremenRes || [])
          .filter((u) => !u.disabled)
          .sort(
            (a, b) =>
              (TYPE_ORDER[a.type_id] ?? 99) - (TYPE_ORDER[b.type_id] ?? 99) ||
              (a.surname || "").localeCompare(b.surname || "") ||
              (a.name || "").localeCompare(b.name || ""),
          );
        const tailgates = tailgatesRes.data || tailgatesRes || [];
        const sseNotes = sseRes.data || sseRes || [];
        const allYearNotes = allYearRes.data || allYearRes || [];

        // Index risks by id
        const risks = {};
        (risksRes.data || risksRes || []).forEach((r) => {
          risks[r.id] = r;
        });

        // Count total year-to-date SSE per foreman
        const sseTotals = {};
        allYearNotes.forEach((n) => {
          if (n.created_by) sseTotals[n.created_by] = (sseTotals[n.created_by] || 0) + 1;
        });

        this.setState({ foremen, tailgates, sseNotes, sseTotals, risks, loading: false });
        this.startRolling(foremen.length);
      })
      .catch(() => this.setState({ loading: false }));
  }

  getWeekDays(weekOffset) {
    const start = moment().add(weekOffset, "week").startOf("isoWeek");
    return Array.from({ length: 5 }, (_, i) => start.clone().add(i, "days"));
  }

  // Saturday and Sunday separating last week from the current week
  getWeekendDays() {
    const start = moment().subtract(1, "week").startOf("isoWeek");
    return [start.clone().add(5, "days"), start.clone().add(6, "days")];
  }

  holdsTailgates(foreman) {
    return foreman.type_id === USER_TYPE_CONTREMAITRE;
  }

  getDayInfo(foreman, day) {
    const { tailgates, sseNotes } = this.state;
    const isFuture = day.isAfter(moment(), "day");

    const hasTailgate = tailgates.some(
      (t) => t.user_id === foreman.id && moment(t.date).isSame(day, "day"),
    );

    const sseCount = sseNotes.filter(
      (n) => n.created_by === foreman.id && moment(n.date).isSame(day, "day"),
    ).length;

    let tileClass;
    if (!this.holdsTailgates(foreman)) tileClass = "na";
    else if (isFuture) tileClass = "future";
    else tileClass = hasTailgate ? "done" : "missed";

    return { tileClass, sseCount, isFuture };
  }

  getSSEPercent(foreman) {
    const total = this.state.sseTotals[foreman.id] || 0;
    const weeksWorked = foreman.weeks_worked || 52;
    return Math.round((total / weeksWorked) * 100);
  }

  getAvatarUrl(foreman) {
    try {
      return foreman.documents[0].url.thumb.url;
    } catch (_) {
      return null;
    }
  }

  getInitials(foreman) {
    return (
      (foreman.name || "").charAt(0).toUpperCase() + (foreman.surname || "").charAt(0).toUpperCase()
    );
  }

  renderWeekBlock(weekDays, foreman, isHeader, labels = DAY_LABELS) {
    if (isHeader) {
      return (
        <div className="tg-week-block">
          <div className="tg-day-labels">
            {labels.map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="tg-week-block">
        <div className="tg-tiles">
          {weekDays.map((day, i) => {
            const { tileClass, sseCount, isFuture } = this.getDayInfo(foreman, day);
            return (
              <div key={i} className={`tg-tile tg-tile--${tileClass}`}>
                {!isFuture && sseCount > 0 && <span className="tg-sse-count">{sseCount}</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Project of the tailgate a foreman held today, with the risks it covers
  getTodayTailgate(foremanId) {
    const { tailgates, risks } = this.state;
    const todayTailgates = tailgates
      .filter((t) => t.user_id === foremanId && moment(t.date).isSame(moment(), "day"))
      .sort((a, b) => new Date(b.date) - new Date(a.date) || b.id - a.id);

    if (!todayTailgates.length) return null;

    const tailgate = todayTailgates[0];

    const riskIds = [];
    (tailgate.means || []).forEach((m) => {
      if (m.risk_id && !riskIds.includes(m.risk_id)) riskIds.push(m.risk_id);
    });

    return {
      project: tailgate.project,
      risks: riskIds.map((id) => risks[id]).filter(Boolean),
    };
  }

  renderRow(foreman, key, days) {
    const { lastWeekDays, weekendDays, thisWeekDays } = days;
    const avatarUrl = this.getAvatarUrl(foreman);
    const percent = this.getSSEPercent(foreman);
    const percentMod = percent >= 100 ? "high" : percent >= 70 ? "mid" : "low";
    const todayTailgate = this.getTodayTailgate(foreman.id);

    return (
      <div key={key} className="tg-row">
        <div className="tg-col-name">
          <div className="tg-avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt={this.getInitials(foreman)} />
            ) : (
              <span>{this.getInitials(foreman)}</span>
            )}
          </div>
          <span className="tg-name">
            {foreman.name} {foreman.surname}
          </span>
        </div>

        <div className="tg-week-section">{this.renderWeekBlock(lastWeekDays, foreman, false)}</div>
        <div className="tg-weekend-section">
          {this.renderWeekBlock(weekendDays, foreman, false)}
        </div>
        <div className="tg-week-section">{this.renderWeekBlock(thisWeekDays, foreman, false)}</div>

        <div className={`tg-col-percent tg-percent--${percentMod}`}>{percent}%</div>

        <div className="tg-col-project">
          {todayTailgate && todayTailgate.project ? (
            <div className="tg-project-info">
              {todayTailgate.project.client && (
                <span className="tg-project-client" title={todayTailgate.project.client.name}>
                  {todayTailgate.project.client.name}
                </span>
              )}
              <span className="tg-project-label" title={todayTailgate.project.label}>
                {todayTailgate.project.label}
              </span>
            </div>
          ) : (
            <span className="tg-project-empty">—</span>
          )}
          {todayTailgate && todayTailgate.risks.length > 0 && (
            <div className="tg-project-pictos">
              {todayTailgate.risks.slice(0, 3).map((r, i) => (
                <img
                  key={i}
                  src={process.env.PUBLIC_URL + "/images/risks/" + (r.picto || "default.png")}
                  alt={r.label}
                  title={r.label}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { foremen, loading } = this.state;

    if (loading) return <div className="tg-state">Chargement...</div>;
    if (!foremen.length) return <div className="tg-state">Aucun utilisateur</div>;

    const { rowHeight, snapping } = this.state;
    const rolling = foremen.length > ROWS_PER_BATCH;

    const lastWeekDays = this.getWeekDays(-1);
    const weekendDays = this.getWeekendDays();
    const thisWeekDays = this.getWeekDays(0);
    const days = { lastWeekDays, weekendDays, thisWeekDays };

    // Pad the short last batch so the clone starts on a batch boundary.
    const spacerCount = rolling ? this.batchCount() * ROWS_PER_BATCH - foremen.length : 0;

    return (
      <div className="tailgate-grid">
        {/* Header row */}
        <div className="tg-header">
          <div className="tg-col-name" />
          <div className="tg-week-section">
            <span className="tg-week-title">Semaine dernière</span>
            {this.renderWeekBlock(lastWeekDays, null, true)}
          </div>
          <div className="tg-weekend-section">
            <span className="tg-week-title">&nbsp;</span>
            {this.renderWeekBlock(weekendDays, null, true, WEEKEND_LABELS)}
          </div>
          <div className="tg-week-section">
            <span className="tg-week-title">Semaine courante</span>
            {this.renderWeekBlock(thisWeekDays, null, true)}
          </div>
          <div className="tg-col-percent">% SSE</div>
          <div className="tg-col-project">Projet du jour</div>
        </div>

        <div
          className="tg-viewport"
          style={rolling ? { height: ROWS_PER_BATCH * rowHeight } : undefined}
        >
          <div
            className={`tg-track${snapping ? " tg-track--snap" : ""}`}
            ref={this.trackRef}
            style={{ transform: `translateY(-${this.batchOffset()}px)` }}
          >
            {foremen.map((foreman) => this.renderRow(foreman, foreman.id, days))}
            {rolling && spacerCount > 0
              ? Array.from({ length: spacerCount }, (_, i) => (
                  <div key={`spacer-${i}`} className="tg-row tg-row--spacer" />
                ))
              : null}
            {rolling
              ? foremen
                  .slice(0, ROWS_PER_BATCH)
                  .map((foreman) => this.renderRow(foreman, `clone-${foreman.id}`, days))
              : null}
          </div>
        </div>
      </div>
    );
  }
}

export default TailgateGrid;
